import { InternalOrganization } from '../../features/organizations/types/Organizations'
import { InternalPermission } from '../../types/InternalPermission'

export type AccessOrganization = Pick<InternalOrganization, 'id' | 'name' | 'contactEmail' | 'userCount'>

export const MAX_ORGANIZATION_SEARCH_RESULTS = 50

// Many organizations on the backend have an empty name.
export const getOrganizationLabel = (organization: AccessOrganization | undefined, id: string) =>
  organization?.name?.trim() || organization?.contactEmail?.trim() || id

// A permission row keeps ids of deleted organizations; they are counted, never shown or sent.
export const getGrantedAccess = (permission: InternalPermission | undefined, organizations: AccessOrganization[]) => {
  const existingIds = new Set(organizations.map(({ id }) => id))
  const ids = Array.from(new Set(permission?.organizationIds ?? []))
  return {
    grantedIds: ids.filter((id) => existingIds.has(id)),
    staleCount: ids.filter((id) => !existingIds.has(id)).length,
  }
}

export const countGrantedOrganizations = (permission: InternalPermission | undefined, existingIds: Set<string>) =>
  new Set((permission?.organizationIds ?? []).filter((id) => existingIds.has(id))).size

export const diffAccess = (initialIds: string[], currentIds: string[]) => ({
  added: currentIds.filter((id) => !initialIds.includes(id)),
  removed: initialIds.filter((id) => !currentIds.includes(id)),
})

export const getUnappliedAccess = (stored: InternalPermission | undefined, added: string[], removed: string[]) => {
  const storedIds = new Set(stored?.organizationIds ?? [])
  return {
    notAdded: added.filter((id) => !storedIds.has(id)),
    notRemoved: removed.filter((id) => storedIds.has(id)),
  }
}

// As legacy: nothing until something is typed, then by user count, most first.
export const searchOrganizations = (organizations: AccessOrganization[], search: string, excludedIds: string[]) => {
  const query = search.trim().toLowerCase()
  if (!query) return []
  return organizations
    .filter(({ id }) => !excludedIds.includes(id))
    .filter(({ name, contactEmail }) => name?.toLowerCase().includes(query) || contactEmail?.toLowerCase().includes(query))
    .sort((a, b) => (b.userCount ?? 0) - (a.userCount ?? 0) || (a.name ?? '').localeCompare(b.name ?? ''))
}
